import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Phone, Award, Users, FileCheck2, MapPin, ArrowRight, ShieldCheck, 
  Clock, Wrench, CheckCircle2, ChevronRight, Star, Headphones, ChevronLeft,
  Search, Check, BookOpen
} from 'lucide-react';
import { getAssetUrl, handleImageError } from '../../utils/assetHelper';
import { BUSINESS_INFO } from '../../utils/constants';
import SearchableSelect from '../../components/ui/SearchableSelect';
import Accordion from '../../components/ui/Accordion';
import SectionHeader from '../../components/ui/SectionHeader';
import rawHyderabadAreas from '../../data/hyderabadAreas.json';
import { CLIENTS_DATA } from '../../data/clientsData';
import { testimonialsData } from '../../data/testimonialsData';
import { api } from '../../api/client';
import { useToast } from '../../context/ToastContext';
import { validateLeadForm } from '../../utils/validation';
import useSEO from '../../hooks/useSEO';

const hyderabadAreasList = rawHyderabadAreas.map(a => ({ value: a, label: a }));

export default function HomePage() {
  useSEO({
    title: `${BUSINESS_INFO.name} | Professional Rolling Shutter Repair, Motors & Installation Hyderabad`,
    description: 'Fast 24/7 rolling shutter repair, motor replacement, spring tensioning & commercial installation across Hyderabad. 60–90 min response, genuine OEM spares.',
    canonical: 'https://shutterservices.in/'
  });

  const toast = useToast();
  const [formState, setFormState] = React.useState({
    name: '',
    phone: '',
    serviceType: '',
    location: '',
    date: '',
    message: '',
    file: null
  });
  const [submitting, setSubmitting] = React.useState(false);
  const [formSuccess, setFormSuccess] = React.useState(false);
  const [formError, setFormError] = React.useState('');

  // Carousel ref and scroll handlers
  const carouselRef = React.useRef(null);
  const scrollCarousel = (direction) => {
    if (carouselRef.current) {
      const scrollAmount = 260;
      carouselRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  // Locality Zone and Search Filter State
  const [selectedZone, setSelectedZone] = React.useState('All Key Hubs');
  const [areaSearch, setAreaSearch] = React.useState('');

  const ZONE_LOCALITIES = React.useMemo(() => ({
    'All Key Hubs': [
      'Gachibowli', 'Kondapur', 'Madhapur', 'Kukatpally', 'Miyapur', 'Banjara Hills',
      'Jubilee Hills', 'Ameerpet', 'Secunderabad', 'Begumpet', 'Uppal', 'LB Nagar',
      'Dilsukhnagar', 'Balanagar', 'Financial District', 'Hitec City', 'Sanath Nagar', 'Kompally'
    ],
    'IT & Cyberabad': [
      'Gachibowli', 'Madhapur', 'Hitec City', 'Kondapur', 'Financial District', 
      'Jubilee Hills', 'Raidurg', 'Manikonda', 'Kokapet', 'Narsingi', 'Hafeezpet', 'Kothaguda'
    ],
    'Secunderabad': [
      'Secunderabad', 'Marredpally', 'Bowenpally', 'Begumpet', 'Alwal', 
      'Trimulgherry', 'Sainikpuri', 'Tarnaka', 'Bolarum', 'Yapral', 'Karkhana', 'Padmarao Nagar'
    ],
    'Central Hyderabad': [
      'Ameerpet', 'Somajiguda', 'Banjara Hills', 'Punjagutta', 'Abids', 
      'Himayatnagar', 'Khairatabad', 'Nampally', 'Basheerbagh', 'Lakdikapul', 'Koti', 'Narayanaguda'
    ],
    'North & Kukatpally': [
      'Kukatpally', 'Miyapur', 'Nizampet', 'Balanagar', 'Jeedimetla', 
      'Kompally', 'Medchal', 'Bachupally', 'Chanda Nagar', 'Pragathi Nagar', 'KPHB Colony', 'Moosapet'
    ],
    'East & Industrial': [
      'Uppal', 'LB Nagar', 'Nacharam', 'Mallapur', 'Dilsukhnagar', 
      'Habsiguda', 'Cherlapally', 'Nagole', 'Ramanthapur', 'Vanasthalipuram', 'Katedan', 'Boduppal'
    ]
  }), []);

  const displayedLocalities = React.useMemo(() => {
    if (areaSearch.trim()) {
      return rawHyderabadAreas
        .filter(a => a.toLowerCase().includes(areaSearch.toLowerCase().trim()))
        .slice(0, 18)
        .map(name => ({
          name,
          slug: name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
        }));
    }
    const hubList = ZONE_LOCALITIES[selectedZone] || ZONE_LOCALITIES['All Key Hubs'];
    return hubList.map(name => ({
      name,
      slug: name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
    }));
  }, [areaSearch, selectedZone, ZONE_LOCALITIES]);

  // File input ref for photo uploads
  const fileInputRef = React.useRef(null);

  // Testimonial slider state
  const [activeTestimonialIdx, setActiveTestimonialIdx] = React.useState(0);

  // Modern Animated Stats Counter
  const [counts, setCounts] = React.useState({ exp: 0, customers: 0, jobs: 0, areas: 0 });
  const statsRef = React.useRef(null);
  const [hasAnimated, setHasAnimated] = React.useState(false);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          const duration = 1600;
          const steps = 40;
          const stepTime = duration / steps;
          let currentStep = 0;

          const timer = setInterval(() => {
            currentStep++;
            const progress = currentStep / steps;
            const easeOutQuad = 1 - (1 - progress) * (1 - progress);

            setCounts({
              exp: Math.round(10 * easeOutQuad),
              customers: Math.round(1500 * easeOutQuad),
              jobs: Math.round(5000 * easeOutQuad),
              areas: Math.round(50 * easeOutQuad)
            });

            if (currentStep >= steps) {
              clearInterval(timer);
              setCounts({ exp: 10, customers: 1500, jobs: 5000, areas: 50 });
            }
          }, stepTime);
        }
      },
      { threshold: 0.2 }
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => observer.disconnect();
  }, [hasAnimated]);

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    // Strict client-side validation
    const validation = validateLeadForm({
      name: formState.name,
      phone: formState.phone,
      serviceType: formState.serviceType,
      location: formState.location,
      file: formState.file
    });

    if (!validation.valid) {
      setFormError(validation.firstError);
      toast.error(validation.firstError, 'Validation Error');
      return;
    }

    setSubmitting(true);
    try {
      let res;
      if (formState.file) {
        const formData = new FormData();
        formData.append('name', formState.name);
        formData.append('phone', validation.cleanedPhone);
        formData.append('service_type', formState.serviceType);
        formData.append('locality', formState.location);
        formData.append('message', formState.message + (formState.date ? ` (Preferred Date: ${formState.date})` : ''));
        formData.append('photo', formState.file);
        res = await api.post('/leads', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        const payload = {
          name: formState.name,
          phone: validation.cleanedPhone,
          service_type: formState.serviceType,
          locality: formState.location,
          message: formState.message + (formState.date ? ` (Preferred Date: ${formState.date})` : '')
        };
        res = await api.post('/leads', payload);
      }

      setFormSuccess(true);
      toast.success(
        res.data?.message || 'Your service request has been received! Our Hyderabad dispatch team will contact you shortly.',
        'Request Submitted'
      );
      setFormState({ name: '', phone: '', serviceType: '', location: '', date: '', message: '', file: null });
    } catch (err) {
      console.error('Lead submission error:', err);
      const errMsg = err.response?.data?.error || err.response?.data?.message || `Could not submit. Please call us at ${BUSINESS_INFO.phone}.`;
      setFormError(errMsg);
      toast.error(errMsg, 'Submission Failed');
    } finally {
      setSubmitting(false);
    }
  };

  const stats = [
    { icon: ShieldCheck, key: 'exp', target: '10+', display: `${counts.exp}+`, label: 'Years of Experience' },
    { icon: Users, key: 'customers', target: '1,500+', display: `${counts.customers.toLocaleString()}+`, label: 'Happy Customers' },
    { icon: FileCheck2, key: 'jobs', target: '5,000+', display: `${counts.jobs.toLocaleString()}+`, label: 'Service Jobs Completed' },
    { icon: MapPin, key: 'areas', target: '50+', display: `${counts.areas}+`, label: 'Service Areas in Hyderabad' },
  ];

  const services = [
    {
      title: 'Rolling Shutter Repair',
      desc: 'Fast and reliable repair for all types of rolling shutters.',
      image: getAssetUrl('assets/images/shutter/service-rolling-shutter-repair.webp'),
      link: '/service/rolling-shutter-repair/',
    },
    {
      title: 'Rolling Shutter Installation',
      desc: 'New shutter installation for shops, offices and industries.',
      image: getAssetUrl('assets/images/shutter/service-installation.webp'),
      link: '/service/shutter-installation/',
    },
    {
      title: 'Shutter Motor Repair',
      desc: 'Motor repair, repacement and automation services.',
      image: getAssetUrl('assets/images/shutter/service-motor-repair.webp'),
      link: '/service/shutter-motor-repair/',
    },
    {
      title: 'Rolling Shutter Maintenance',
      desc: 'Regular maintenance to ensure smooth operation.',
      image: getAssetUrl('assets/images/shutter/service-maintenance.webp'),
      link: '/service/shutter-maintenance/',
    },
    {
      title: 'Automatic Shutter Services',
      desc: 'Remote and sensor based automatic shutter solutions.',
      image: getAssetUrl('assets/images/shutter/service-automatic.webp'),
      link: '/service/automatic-shutter-services/',
    },
    {
      title: 'Commercial & Industrial',
      desc: 'Heavy duty shutter solutions for warehouses and factories.',
      image: getAssetUrl('assets/images/shutter/service-commercial.webp'),
      link: '/service/commercial-shutter-services/',
    },
  ];

  const benefits = [
    { icon: Wrench, title: 'Experienced Technicians', desc: 'Skilled and trained professionals' },
    { icon: FileCheck2, title: 'Transparent Pricing', desc: 'No hidden charges' },
    { icon: CheckCircle2, title: 'Genuine Parts', desc: 'Quality materials and components' },
    { icon: ShieldCheck, title: 'Quality Assurance', desc: 'Thorough multi-point quality testing' },
    { icon: Clock, title: 'On-Time Service', desc: 'We value your time' },
    { icon: Headphones, title: 'Customer Support', desc: 'Always here to help' },
  ];

  const steps = [
    { num: '01', title: 'Request Service', desc: 'Call or fill enquiry form' },
    { num: '02', title: 'Inspection', desc: 'Site inspection by technician' },
    { num: '03', title: 'Quotation', desc: 'Get detailed quotation' },
    { num: '04', title: 'Approval', desc: 'Confirm and schedule' },
    { num: '05', title: 'Service', desc: 'Repair or installation work' },
    { num: '06', title: 'Completion', desc: 'Job completion & testing sign-off' },
  ];

  const areas = [
    { name: 'Gachibowli', image: getAssetUrl('assets/images/service-areas/gachibowli.webp'), link: '/rolling-shutter-repair-in-gachibowli-premier-shutter-service/' },
    { name: 'Kondapur', image: getAssetUrl('assets/images/service-areas/kondapur.webp'), link: '/rolling-shutter-repair-in-kondapur-fast-solutions/' },
    { name: 'Madhapur', image: getAssetUrl('assets/images/service-areas/madhapur.webp'), link: '/rolling-shutter-repair-in-madhapur-affordable-rates/' },
    { name: 'Kukatpally', image: getAssetUrl('assets/images/service-areas/kukatpally.webp'), link: '/rolling-shutter-repair-in-kukatpally-trusted-service/' },
    { name: 'Miyapur', image: getAssetUrl('assets/images/service-areas/miyapur.webp'), link: '/rolling-shutter-repair-in-miyapur-top-notch-rolling-shutter-repair-and-maintenance-in-miyapur/' },
    { name: 'Banjara Hills', image: getAssetUrl('assets/images/service-areas/banjara-hills.webp'), link: '/rolling-shutter-repair-in-banjara-hills-top-quality-shutter-repair-solutions-in-banjara-hills/' },
  ];

  const projects = [
    { title: 'Shop Shutter Installation', loc: 'Kondapur, Hyderabad', image: getAssetUrl('assets/images/shutter/project-shop.webp'), link: '/our-works/devidayal-solar-solutions-kondapur-1/' },
    { title: 'Motor Replacement', loc: 'Gachibowli, Hyderabad', image: getAssetUrl('assets/images/shutter/project-motor.webp'), link: '/our-works/alembic-pharma-depot-gachibowli-2/' },
    { title: 'Commercial Shutter', loc: 'Madhapur, Hyderabad', image: getAssetUrl('assets/images/shutter/project-commercial.webp'), link: '/our-works/decathlon-hub-madhapur-3/' },
    { title: 'Industrial Shutter', loc: 'Balanagar, Hyderabad', image: getAssetUrl('assets/images/shutter/project-industrial.webp'), link: '/our-works/voltas-air-conditioning-depot-balanagar-36/' },
    { title: 'Automatic Shutter', loc: 'Jubilee Hills, Hyderabad', image: getAssetUrl('assets/images/shutter/project-automatic.webp'), link: '/our-works/heritage-foods-distribution-jubilee-hills-5/' },
  ];

  const totalTestimonialPages = Math.ceil(testimonialsData.length / 3);
  const currentTestimonials = testimonialsData.slice(
    activeTestimonialIdx * 3,
    activeTestimonialIdx * 3 + 3
  );

  const featuredBlogs = [
    {
      date: 'February 15, 2026',
      title: 'Why Is Your Rolling Shutter Stuck Halfway? Step-by-Step Diagnostic & Safe Resolution',
      image: getAssetUrl('assets/images/shutter/blog-2.webp'),
      link: '/blog/rolling-shutter-stuck-halfway-diagnostic-guide/',
      category: 'Troubleshooting Guide',
      readTime: '6 min read',
      author: 'Technical Team',
      excerpt: 'Step-by-step diagnostic protocol for stuck rolling shutters in Hyderabad. Inspect torsion spring fatigue, bent guide rails, and motor limit switches.'
    },
    {
      date: 'February 10, 2026',
      title: 'Motor Hums But Shutter Does Not Move: Diagnosing Capacitor, Gearbox & Limit Faults',
      image: getAssetUrl('assets/images/shutter/blog-1.webp'),
      link: '/blog/motor-hums-shutter-does-not-open-capacitor-gearbox-faults/',
      category: 'Motor & Electrical',
      readTime: '5 min read',
      author: 'Maheshwara Rao',
      excerpt: 'Comprehensive troubleshooting for motor hums, dead run capacitors, gearbox jams, and limit switch calibration across commercial facilities.'
    },
    {
      date: 'January 28, 2026',
      title: 'Comprehensive Rolling Shutter Maintenance Guide for Commercial Establishments',
      image: getAssetUrl('assets/images/shutter/blog-3.webp'),
      link: '/blog/commercial-rolling-shutter-preventive-maintenance-guide/',
      category: 'Maintenance Tips',
      readTime: '9 min read',
      author: 'Maheshwara Rao',
      excerpt: 'Authoritative maintenance checklist covering IS 6248 compliance, lubrication protocols, and preventive servicing for retail and warehouse shutters.'
    }
  ];

  const faqItems = [
    {
      q: 'How fast can a technician arrive for emergency shutter repair in Hyderabad?',
      a: 'We operate mobile technician service teams across Gachibowli, Kukatpally, Secunderabad, Ameerpet, and LB Nagar corridors. For emergency jams or locked shutters, our technicians reach your premises within 60 to 90 minutes anywhere in Greater Hyderabad.'
    },
    {
      q: 'Can a burned or jammed shutter motor be repaired on-site?',
      a: 'Yes. Our technicians carry specialized diagnostic equipment, replacement capacitors, carbon brushes, and rewound coils. Minor electrical, gear, and limit-switch issues are fixed on-site within 2 hours. If full stator rewinding is necessary, we provide temporary security lock-down.'
    },
    {
      q: 'Do you provide genuine parts and quality assurance?',
      a: 'Yes. All genuine replacement springs, motors, bearings, and slat panels come with manufacturer warranties on OEM components. In addition, every repair undergoes strict multi-point testing with digital completion sign-off.'
    },
    {
      q: 'How much does rolling shutter repair cost in Hyderabad?',
      a: 'Basic on-site inspection and lubrication starts with nominal charges. Slat realignment, spring tensioning, and motor servicing are quoted transparently with zero hidden fees before work starts. You receive a complete digital quotation.'
    },
    {
      q: 'Can you convert our manual pull-push shop shutter to automatic motorized?',
      a: 'Yes, absolutely. We specialize in converting existing manual commercial and garage rolling shutters into motorized setups by retrofitting high-torque center motors or side motors with wireless remote controls, without needing to replace the entire shutter body.'
    },
    {
      q: 'Do you offer Annual Maintenance Contracts (AMC) for commercial complexes?',
      a: 'Yes, we provide quarterly and bi-annual AMC packages for retail showrooms, banks, warehouses, and industrial units, including regular tension adjustments, lubrication, safety alignment checks, and priority breakdown response.'
    },
    {
      q: 'What are the warning signs that a shutter spring is about to snap?',
      a: 'Key warning signs include: the shutter feeling unusually heavy to lift manually, a loud squealing or creaking sound from the overhead barrel box, uneven side-to-side tilting when moving, or the shutter refusing to stay open at mid-height. Contact us immediately to replace worn springs before sudden collapse.'
    },
    {
      q: 'What should I do if power fails and our motorized shutter is shut?',
      a: 'All our motorized shutter installations and conversions feature a built-in manual chain override mechanism or an emergency release clutch. You can effortlessly hoist or lower the shutter manually using the manual pull chain even during prolonged power outages.'
    },
    {
      q: 'Can individual bent or dented slats be replaced without buying a new shutter?',
      a: 'Yes! If a vehicle, forklift, or delivery trolley has bumped your shutter, our technicians can disassemble and slide out only the damaged slats and interlock new matching gauge steel or aluminum slats, restoring smooth operation at a fraction of full replacement cost.'
    },
    {
      q: 'Are your emergency repair technicians available on Sundays and holidays?',
      a: 'Yes, our emergency standby vans operate 24 hours a day, 7 days a week, including Sundays and public holidays. When commercial stores, medical centers, or logistics warehouses face an unexpected jam, our helpline is always manned.'
    },
    {
      q: 'How often should commercial or warehouse rolling shutters be serviced?',
      a: 'For standard commercial shops opening 2 to 4 times daily, bi-annual servicing (every 6 months) is optimal. For high-cycle industrial loading bays, logistics hubs, and basement car parks operating dozens of cycles daily, quarterly lubrication and spring balancing are strongly advised.'
    },
    {
      q: 'Which payment options are accepted after the shutter repair is completed?',
      a: 'We accept all standard payment methods including UPI (Google Pay, PhonePe, Paytm), Net Banking / IMPS, Credit & Debit Cards, and Cash. For corporate enterprises and AMC accounts, official GST tax invoices and PO-based billing are available.'
    }
  ];

  const faqCol1 = faqItems.slice(0, 6);
  const faqCol2 = faqItems.slice(6);


  return (
    <div className="w-full bg-[#f8fafc]">
      {/* ==================== 1. HERO SECTION ==================== */}
      <section className="relative pt-8 pb-12 border-b border-gray-100 bg-[#ecf5fc] z-20">
        {/* Hero Background Image — full technician with logo fully visible */}
        <div className="absolute inset-0 z-0 select-none pointer-events-none overflow-hidden">
          <picture>
            <source media="(max-width: 767px)" srcSet={getAssetUrl('assets/images/shutter/hero-mobile.webp')} />
            <img
              src={getAssetUrl('assets/images/shutter/hero-panoramic-bg.webp')}
              alt="Rolling Shutter Repair Professional Hyderabad"
              width="1440"
              height="550"
              fetchPriority="high"
              loading="eager"
              decoding="async"
              className="w-full h-full object-cover hero-bg-technician"
            />
          </picture>
          {/* Left gradient for text readability without washing out technician */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#ecf5fc] from-15% via-[#ecf5fc]/80 via-34% to-transparent" />
          {/* Right gradient to blend form area */}
          <div className="absolute inset-0 bg-gradient-to-l from-[#ecf5fc]/40 via-transparent to-transparent" />
          {/* Mobile overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#ecf5fc] via-[#ecf5fc]/95 to-[#ecf5fc]/80 lg:hidden" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-center">
            
            {/* Left Column (Hero Headline, Actions & Trust Badges) */}
            <div className="lg:col-span-7 space-y-4">
              
              <div className="flex items-center gap-2 text-[#00796b] text-[12px] font-bold tracking-wider uppercase">
                <span className="w-2 h-2 rounded-full bg-[#00796b] inline-block shrink-0" />
                <span>Rolling Shutter Services in Hyderabad</span>
              </div>

              <h1 className="text-3xl sm:text-4xl lg:text-[42px] font-extrabold text-[#0d2137] leading-[1.12] tracking-tight">
                Expert Rolling Shutter <br className="hidden sm:inline" />
                Repair &amp; Installation <br className="hidden sm:inline" />
                Services
              </h1>

              <p className="text-sm sm:text-[15px] text-[#4a5568] max-w-lg font-normal leading-relaxed">
                Fast, reliable and professional rolling shutter services for shops, offices, warehouses and industrial spaces across Hyderabad.
              </p>

              {/* Action Buttons */}
              <div className="flex flex-row items-center gap-2 sm:gap-3 pt-1 w-full max-w-md">
                <Link
                  to="/contact-us/"
                  className="flex-1 sm:flex-initial h-[42px] sm:h-[44px] bg-[#00796b] hover:bg-[#004d40] text-white text-[11px] sm:text-sm font-bold px-2.5 sm:px-6 rounded-md transition-all shadow-md shadow-[#00796b]/20 flex items-center justify-center gap-1 sm:gap-2 hover:translate-y-[-1px] cursor-pointer whitespace-nowrap"
                >
                  <span>Request Service</span>
                  <span>&rarr;</span>
                </Link>

                <a
                  href={BUSINESS_INFO.phoneTel}
                  className="flex-1 sm:flex-initial h-[42px] sm:h-[44px] border border-gray-200 hover:border-[#00796b] text-[#0d2137] hover:bg-white text-[11px] font-bold px-2 sm:px-4 rounded-md transition-all flex items-center justify-center gap-1.5 sm:gap-2 bg-white shadow-xs whitespace-nowrap"
                >
                  <Phone className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#00796b] shrink-0 fill-current" />
                  <div className="text-left shrink-0">
                    <span className="text-[9px] sm:text-[10px] text-gray-500 block leading-none font-medium mb-0.5">Call Now</span>
                    <span className="leading-none font-bold text-[11px] sm:text-sm text-[#0d2137] whitespace-nowrap">{BUSINESS_INFO.phone}</span>
                  </div>
                </a>
              </div>

              {/* 4 Feature Badges */}
              <div className="pt-2 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-gray-600 font-semibold">
                <div className="flex items-center gap-1.5">
                  <CheckCircle2 className="w-4 h-4 text-[#00796b] shrink-0" />
                  <span>Fast Response</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <CheckCircle2 className="w-4 h-4 text-[#00796b] shrink-0" />
                  <span>Skilled Technicians</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <CheckCircle2 className="w-4 h-4 text-[#00796b] shrink-0" />
                  <span>Genuine Parts</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <CheckCircle2 className="w-4 h-4 text-[#00796b] shrink-0" />
                  <span>Transparent Pricing</span>
                </div>
              </div>

            </div>

            {/* Right Column: Hero Service Request Form */}
            <div className="lg:col-span-5 flex justify-center lg:justify-end" id="request-form">
              <div className="w-full max-w-[395px] bg-white rounded-2xl border border-gray-100 p-5 sm:p-5.5 shadow-xl shadow-gray-200/60">
                <div className="mb-4">
                  <h2 className="text-base font-extrabold text-[#0f172a]">Request a Service</h2>
                  <p className="text-xs text-[#00796b] flex items-center gap-1 font-medium mt-0.5">
                    <Wrench className="w-3.5 h-3.5" />
                    <span>Get a quick response from our team</span>
                  </p>
                </div>

                {formSuccess ? (
                  <div className="p-6 bg-[#e6faf8] border border-[#00796b]/30 rounded-xl text-center space-y-3">
                    <CheckCircle2 className="w-10 h-10 text-[#00796b] mx-auto" />
                    <div className="text-sm font-bold text-[#0f172a]">Request Sent Successfully!</div>
                    <p className="text-xs text-gray-600">Our technician will call you within 15 minutes to confirm the site inspection.</p>
                    <button
                      type="button"
                      onClick={() => setFormSuccess(false)}
                      className="text-xs font-bold text-[#00796b] underline pt-2"
                    >
                      Submit another enquiry
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleLeadSubmit} className="space-y-3 relative z-20">
                    <div className="grid grid-cols-2 gap-2.5 relative z-10">
                      <div>
                        <label htmlFor="lead-name" className="block text-[11px] font-semibold text-gray-700 mb-1">Full Name *</label>
                        <input
                          id="lead-name"
                          name="name"
                          type="text"
                          required
                          value={formState.name}
                          onChange={(e) => setFormState({ ...formState, name: e.target.value })}
                          placeholder="Enter your name"
                          autoComplete="name"
                          className="w-full px-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:border-[#00796b] bg-gray-50/50"
                        />
                      </div>
                      <div>
                        <label htmlFor="lead-phone" className="block text-[11px] font-semibold text-gray-700 mb-1">Phone Number *</label>
                        <input
                          id="lead-phone"
                          name="phone"
                          type="tel"
                          required
                          value={formState.phone}
                          onChange={(e) => setFormState({ ...formState, phone: e.target.value })}
                          placeholder="Enter phone number"
                          autoComplete="tel"
                          className="w-full px-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:border-[#00796b] bg-gray-50/50"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2.5 relative z-30">
                      <div className="relative">
                        <label className="block text-[11px] font-semibold text-gray-700 mb-1">Service Type *</label>
                        <SearchableSelect
                          options={[
                            { value: 'Rolling Shutter Repair', label: 'Rolling Shutter Repair', desc: 'Emergency jam, spring, and slat alignment' },
                            { value: 'Rolling Shutter Installation', label: 'Shutter Installation', desc: 'New manual or automated shutters' },
                            { value: 'Shutter Motor Repair', label: 'Motor Repair', desc: 'Motor rewinding, gear repair, remote setup' },
                            { value: 'Rolling Shutter Maintenance', label: 'Shutter Maintenance', desc: 'Routine servicing & lubrication' },
                            { value: 'Automatic Shutter Services', label: 'Automatic Shutter', desc: 'Sensor & remote control conversions' },
                            { value: 'Commercial Shutter Services', label: 'Commercial Shutter', desc: 'Durable shop & retail front solutions' },
                            { value: 'Industrial Shutter Services', label: 'Industrial Shutter', desc: 'Heavy-duty motorized factory shutters' },
                            { value: 'Emergency Shutter Repair', label: 'Emergency Repair', desc: '60-90 min rapid dispatch in Hyderabad' },
                          ]}
                          value={formState.serviceType}
                          onChange={(val) => setFormState({ ...formState, serviceType: val })}
                          placeholder="Select Service"
                          searchPlaceholder="Search service..."
                          required
                          icon={Wrench}
                        />
                      </div>

                      <div className="relative">
                        <label className="block text-[11px] font-semibold text-gray-700 mb-1">Location *</label>
                        <SearchableSelect
                          options={hyderabadAreasList}
                          value={formState.location}
                          onChange={(val) => setFormState({ ...formState, location: val })}
                          placeholder="Select Area"
                          searchPlaceholder="Type area name (e.g. Kondapur, Gachibowli)..."
                          required
                          icon={MapPin}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2.5 relative z-10">
                      <div>
                        <label htmlFor="lead-date" className="block text-[11px] font-semibold text-gray-700 mb-1">Preferred Date</label>
                        <input
                          id="lead-date"
                          name="date"
                          type="date"
                          value={formState.date}
                          onChange={(e) => setFormState({ ...formState, date: e.target.value })}
                          className="w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:border-[#00796b] bg-gray-50/50 text-gray-700"
                        />
                      </div>
                      <div>
                        <label htmlFor="lead-message" className="block text-[11px] font-semibold text-gray-700 mb-1">Message</label>
                        <input
                          id="lead-message"
                          name="message"
                          type="text"
                          value={formState.message}
                          onChange={(e) => setFormState({ ...formState, message: e.target.value })}
                          placeholder="Tell us about the issue"
                          className="w-full px-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:border-[#00796b] bg-gray-50/50"
                        />
                      </div>
                    </div>

                    {/* Upload Photos Box */}
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={(e) => {
                        if (e.target.files && e.target.files[0]) {
                          setFormState({ ...formState, file: e.target.files[0] });
                        }
                      }}
                      accept="image/png, image/jpeg, image/jpg"
                      className="hidden"
                    />
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      className="border border-dashed border-gray-300 rounded-lg p-2.5 bg-gray-50 text-center hover:bg-gray-100/60 transition-colors cursor-pointer"
                    >
                      <div className="text-[11px] font-bold text-gray-600 flex items-center justify-center gap-1.5">
                        <Award className="w-3.5 h-3.5 text-[#00796b]" />
                        <span>{formState.file ? formState.file.name : 'Upload Photos (Optional)'}</span>
                      </div>
                      <span className="text-[10px] text-gray-500 block">
                        {formState.file ? `${(formState.file.size / 1024).toFixed(0)} KB - Click to replace` : 'JPG, PNG (Max 5 MB)'}
                      </span>
                    </div>

                    {formError && (
                      <p className="text-[11px] text-red-600 font-semibold" role="alert">{formError}</p>
                    )}

                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full bg-[#00796b] hover:bg-[#004d40] text-white text-xs font-bold py-3 rounded-lg transition-colors shadow-sm flex items-center justify-center gap-2 cursor-pointer"
                    >
                      <span>{submitting ? 'Submitting...' : 'Submit Request'}</span>
                      <span>&rarr;</span>
                    </button>
                  </form>
                )}
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ==================== 2. STATISTICS BAR (MODERN ANIMATED) ==================== */}
      <section ref={statsRef} className="bg-white border-b border-gray-100 shadow-xs relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-4 sm:py-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-0">
            {stats.map((item, idx) => {
              const Icon = item.icon;
              return (
                <div
                  key={idx}
                  className={`group relative flex items-center justify-center gap-4 py-3 md:py-4 px-3 sm:px-6 transition-all duration-300 hover:scale-[1.02] ${
                    idx < 3 ? 'md:border-r md:border-gray-200/80' : ''
                  }`}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-teal-50/0 via-teal-50/40 to-teal-50/0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />

                  <div className="relative w-12 h-12 rounded-xl bg-gradient-to-br from-[#e6faf8] to-[#ccf2ee] text-[#00796b] flex items-center justify-center shrink-0 shadow-xs group-hover:shadow-md group-hover:shadow-[#00796b]/20 group-hover:from-[#00796b] group-hover:to-[#00796b] group-hover:text-white transition-all duration-300">
                    <Icon className="w-5 h-5 transition-transform duration-300 group-hover:scale-110" />
                  </div>

                  <div className="relative">
                    <div className="text-2xl sm:text-3xl font-extrabold text-[#0f172a] tracking-tight leading-none group-hover:text-[#00796b] transition-colors duration-200">
                      {hasAnimated ? item.display : item.target}
                    </div>
                    <div className="text-[12px] sm:text-xs text-gray-500 font-semibold mt-1">
                      {item.label}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ==================== 2.5 TRUSTED CLIENTS STRIP (ANIMATED SINGLE ROW - CONSTRAINED) ==================== */}
      <section className="bg-gray-50/80 border-b border-gray-200/60 py-6 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-[#00796b] inline-block shrink-0 animate-pulse" />
              <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                Trusted by 500+ Businesses Across Hyderabad &amp; Telangana
              </span>
            </div>
            <Link
              to="/our-clients/"
              className="text-xs font-bold text-[#00796b] hover:text-[#00796b] flex items-center gap-1 group transition-colors self-start sm:self-auto"
            >
              <span>View All 100+ Client Partners</span>
              <span className="group-hover:translate-x-1 transition-transform">&rarr;</span>
            </Link>
          </div>

          {/* Continuous Single-Row Infinite Marquee Track with Side Fade Masks (Container Width) */}
          <div className="relative overflow-hidden py-1 rounded-2xl">
            {/* Left Gradient Fade */}
            <div className="pointer-events-none absolute inset-y-0 left-0 w-12 sm:w-20 bg-gradient-to-r from-gray-50/95 to-transparent z-10" />
            {/* Right Gradient Fade */}
            <div className="pointer-events-none absolute inset-y-0 right-0 w-12 sm:w-20 bg-gradient-to-l from-gray-50/95 to-transparent z-10" />

            {/* Marquee Track with duplicated items for seamless loop */}
            <div className="animate-client-marquee flex items-center gap-4">
              {[...CLIENTS_DATA.slice(0, 24), ...CLIENTS_DATA.slice(0, 24)].map((client, idx) => (
                <Link
                  key={`${client.id}-${idx}`}
                  to="/our-clients/"
                  className="w-36 sm:w-44 h-16 bg-white rounded-xl border border-gray-200/80 p-2 sm:p-2.5 flex items-center justify-center shadow-2xs hover:shadow-md hover:border-[#00796b]/40 transition-all flex-none shrink-0 group"
                  title={`${client.name} - ${client.sector}`}
                >
                  <img
                    src={getAssetUrl(client.logo)}
                    alt={`${client.name} Logo`}
                    width="120"
                    height="40"
                    className="max-h-full max-w-full object-contain filter grayscale opacity-80 group-hover:grayscale-0 group-hover:opacity-100 group-hover:scale-105 transition-all duration-300"
                    loading="lazy"
                  />
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ==================== 3. OUR SERVICES — CONTAINER CAROUSEL WITH ARROWS ==================== */}
      <section className="py-14 bg-[#f8fafc]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-8 gap-4">
            <div>
              <div className="text-xs font-bold text-[#00796b] uppercase tracking-wider mb-1">Our Services</div>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-[#0f172a] tracking-tight">Complete Rolling Shutter Solutions</h2>
            </div>
            
            <div className="flex items-center gap-3">
              <Link
                to="/our-services/"
                className="hidden sm:inline-flex items-center gap-1.5 text-xs font-bold text-[#00796b] hover:text-white border border-[#00796b] hover:bg-[#00796b] px-4 py-2 rounded-lg transition-colors bg-white shadow-2xs mr-1"
              >
                <span>View All Services</span>
                <span>&rarr;</span>
              </Link>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => scrollCarousel('left')}
                  className="w-10 h-10 rounded-lg bg-white border border-gray-200 text-gray-700 hover:text-white hover:bg-[#00796b] hover:border-[#00796b] flex items-center justify-center shadow-xs transition-all duration-200 active:scale-95 focus:outline-none focus:ring-2 focus:ring-[#00796b]/30 cursor-pointer"
                  aria-label="Previous service"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  type="button"
                  onClick={() => scrollCarousel('right')}
                  className="w-10 h-10 rounded-lg bg-white border border-gray-200 text-gray-700 hover:text-white hover:bg-[#00796b] hover:border-[#00796b] flex items-center justify-center shadow-xs transition-all duration-200 active:scale-95 focus:outline-none focus:ring-2 focus:ring-[#00796b]/30 cursor-pointer"
                  aria-label="Next service"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>

          <div className="relative">
            <div
              ref={carouselRef}
              className="flex gap-4 sm:gap-5 overflow-x-auto scroll-smooth py-2 px-1 no-scrollbar"
            >
              {services.map((item, idx) => (
                <div
                  key={idx}
                  className="w-[240px] sm:w-[260px] shrink-0 bg-white rounded-xl border border-gray-200/90 shadow-xs hover:shadow-xl hover:border-[#00796b]/40 transition-all duration-300 overflow-hidden flex flex-col justify-between group"
                >
                  <div>
                    <Link to={item.link} className="block h-40 overflow-hidden bg-gray-100 relative">
                      <img
                        src={item.image}
                        alt={item.title}
                        width="260"
                        height="160"
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-108 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    </Link>
                    <div className="p-4">
                      <Link to={item.link} className="block">
                        <h3 className="text-sm font-bold text-[#0f172a] mb-1.5 leading-snug group-hover:text-[#00796b] transition-colors">{item.title}</h3>
                      </Link>
                      <p className="text-[12px] text-gray-500 leading-relaxed line-clamp-2">{item.desc}</p>
                    </div>
                  </div>
                  <div className="p-4 pt-0">
                    <Link
                      to={item.link}
                      aria-label={`Explore Service – ${item.title} in Hyderabad`}
                      className="inline-flex items-center gap-1.5 text-xs font-bold text-[#00796b] group-hover:text-[#00796b] transition-colors"
                    >
                      <span aria-hidden="true">Explore Service</span>
                      <ChevronRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" aria-hidden="true" />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ==================== 4. WHY CHOOSE US ==================== */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            
            <div className="lg:col-span-5 space-y-5">
              <div className="text-xs font-bold text-[#00796b] uppercase tracking-wider">Why Choose Us</div>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-[#0f172a] leading-tight">
                Your Trusted Shutter Service Partner in Hyderabad
              </h2>
              <p className="text-sm text-gray-600 leading-relaxed">
                We are committed to providing high-quality rolling shutter services with professional technicians, genuine parts and reliable support.
              </p>
              <div>
                <Link
                  to="/about-us/"
                  className="inline-flex items-center gap-2 bg-[#00796b] hover:bg-[#00796b] text-white text-xs font-bold px-5 py-3 rounded-md transition-colors shadow-2xs"
                >
                  <span>Learn More About Us</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </div>

            <div className="lg:col-span-7">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {benefits.map((b, idx) => {
                  const Icon = b.icon;
                  return (
                    <div key={idx} className="p-4 rounded-xl border border-gray-200 bg-[#f7f9fc] flex items-start gap-3.5 hover:border-[#00796b] transition-colors">
                      <div className="w-10 h-10 rounded-lg bg-[#e6faf8] text-[#00796b] flex items-center justify-center shrink-0">
                        <Icon className="w-5 h-5" />
                      </div>
                      <div>
                        <h3 className="text-xs font-bold text-[#0f172a] mb-0.5">{b.title}</h3>
                        <p className="text-[11px] text-gray-500 leading-relaxed">{b.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ==================== 5. HOW IT WORKS ==================== */}
      <section className="py-16 bg-[#f7f9fc]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-12 gap-4">
            <div>
              <div className="text-xs font-bold text-[#00796b] uppercase tracking-wider mb-1">How It Works</div>
              <Link to="/how-it-works/">
                <h2 className="text-2xl sm:text-3xl font-extrabold text-[#0f172a] hover:text-[#00796b] transition-colors">Simple Service Process</h2>
              </Link>
            </div>
            <Link
              to="/how-it-works/"
              className="inline-flex items-center gap-1.5 text-xs font-bold text-[#00796b] hover:text-white border border-[#00796b] hover:bg-[#00796b] px-4 py-2 rounded-md transition-colors bg-white shadow-2xs self-start sm:self-auto"
            >
              <span>Explore Full Process</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {steps.map((st, idx) => (
              <div key={idx} className="relative bg-white p-4 rounded-xl border border-gray-200 text-center flex flex-col items-center shadow-2xs">
                <div className="w-10 h-10 rounded-full bg-[#00796b] text-white font-extrabold text-xs flex items-center justify-center mb-3 shadow-xs">
                  {st.num}
                </div>
                <h3 className="text-xs font-bold text-[#0f172a] mb-1">{st.title}</h3>
                <p className="text-[11px] text-gray-500 leading-normal">{st.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== 6. COMPLETE SHUTTER TYPES WE REPAIR & INSTALL ==================== */}
      <section className="py-16 bg-[#f8fafc] border-t border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-8 gap-4">
            <div>
              <div className="text-xs font-bold text-[#00796b] uppercase tracking-wider mb-1">Engineered For Durability</div>
              <Link to="/shutter-types/">
                <h2 className="text-2xl sm:text-3xl font-extrabold text-[#0f172a] hover:text-[#00796b] transition-colors">
                  Shutter Types We Manufacture, Install &amp; Service
                </h2>
              </Link>
              <p className="text-xs sm:text-sm text-gray-500 mt-1 max-w-2xl">
                From compact shop fronts to heavy automated industrial factory warehouses, we service all rolling shutter mechanisms across Greater Hyderabad.
              </p>
            </div>
            <Link
              to="/shutter-types/"
              className="inline-flex items-center gap-1.5 text-xs font-bold text-[#00796b] hover:text-white border border-[#00796b] hover:bg-[#00796b] px-4 py-2 rounded-md transition-colors bg-white shadow-2xs self-start sm:self-auto shrink-0"
            >
              <span>Explore All Shutter Types</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                title: 'Motorized Rolling Shutters',
                desc: 'Single & three-phase heavy copper wound motors with remote control, emergency manual override & push-button stations.',
                image: getAssetUrl('assets/images/shutter/service-automatic.webp'),
                features: ['Smart Remote & Sensor Automation', 'Side Motor & Center Motor Setup', 'Battery UPS Backup Compatible'],
                badge: 'High Demand',
                badgeColor: 'bg-emerald-700 text-white',
                quoteParam: 'Automatic Shutter Services'
              },
              {
                title: 'Manual Gear & Pull-Push Shutters',
                desc: 'Smooth counterbalanced spring loaded shutters for retail stores, commercial shops, and garages.',
                image: getAssetUrl('assets/images/shutter/type-galvanized-solid.webp'),
                features: ['High-Tension Coil Springs', 'Ball Bearing Side Guides', 'Reinforced Bottom Locking Bar'],
                badge: 'Retail Standard',
                badgeColor: 'bg-[#00695c] text-white',
                quoteParam: 'Rolling Shutter Installation'
              },
              {
                title: 'Perforated & Grill Shutters',
                desc: 'See-through ventilation & display security shutters ideal for showroom facades, malls & banking kiosks.',
                image: getAssetUrl('assets/images/shutter/type-perforated.webp'),
                features: ['Optimal Air Circulation', '24/7 Window Display Visibility', 'High Tensile Steel Slats'],
                badge: 'Showroom Pick',
                badgeColor: 'bg-indigo-700 text-white',
                quoteParam: 'Rolling Shutter Installation'
              },
              {
                title: 'Polycarbonate Transparent Shutters',
                desc: 'Crystal-clear impact-resistant polycarbonate slats providing aesthetic modern visibility with burglar-proof strength.',
                image: getAssetUrl('assets/images/shutter/type-polycarbonate.webp'),
                features: ['90% Optical Transparency', 'UV Stabilized & Scratch Resistant', 'Prestige Mall Showroom Grade'],
                badge: 'Modern Luxury',
                badgeColor: 'bg-cyan-800 text-white',
                quoteParam: 'Rolling Shutter Installation'
              },
              {
                title: 'Heavy-Duty Industrial Shutters',
                desc: 'Double-wall galvanized steel (GI) and extruded aluminium slats built for warehouse logistics & dock loading bays.',
                image: getAssetUrl('assets/images/shutter/service-industrial.webp'),
                features: ['High Wind Resistance Rating', 'Industrial Heavy Gearbox Motors', 'Continuous Cycle Durability'],
                badge: 'Factory Grade',
                badgeColor: 'bg-amber-800 text-white',
                quoteParam: 'Commercial & Industrial Shutters'
              },
              {
                title: 'Fire Rated & Insulated Shutters',
                desc: 'Specially fabricated acoustic & thermal insulated shutters designed for industrial fire compartments.',
                image: getAssetUrl('assets/images/shutter/type-fire-rated.webp'),
                features: ['Polyurethane Foam Insulation', 'Auto Drop Emergency Sensor', 'Heavy Gauge Steel Casing'],
                badge: 'Safety Certified',
                badgeColor: 'bg-rose-700 text-white',
                quoteParam: 'Rolling Shutter Installation'
              },
            ].map((st, i) => (
              <div 
                key={i} 
                className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-xs hover:shadow-xl hover:border-[#00796b]/50 transition-all duration-300 flex flex-col justify-between group"
              >
                <div>
                  {/* Card Image Banner with Badge */}
                  <div className="relative h-44 sm:h-48 w-full overflow-hidden bg-gray-100">
                    <img
                      src={st.image}
                      alt={st.title}
                      width="380"
                      height="192"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent opacity-70 group-hover:opacity-50 transition-opacity" />
                    <div className="absolute top-3 left-3">
                      <span className={`text-[10px] font-extrabold uppercase tracking-wider px-3 py-1 rounded-full shadow-sm ${st.badgeColor}`}>
                        {st.badge}
                      </span>
                    </div>
                    <div className="absolute bottom-3 left-3 right-3">
                      <span className="text-white text-xs font-semibold drop-shadow-sm flex items-center gap-1.5">
                        <Wrench className="w-3.5 h-3.5 text-teal-300" />
                        <span>Fabricated &amp; Serviced by In-House Engineers</span>
                      </span>
                    </div>
                  </div>

                  {/* Card Content Body */}
                  <div className="p-5 sm:p-6">
                    <h3 className="text-base sm:text-lg font-bold text-[#0f172a] group-hover:text-[#00796b] transition-colors mb-2">
                      {st.title}
                    </h3>
                    <p className="text-xs text-gray-500 leading-relaxed mb-4 line-clamp-2">
                      {st.desc}
                    </p>
                    <ul className="space-y-1.5 border-t border-gray-100 pt-3">
                      {st.features.map((feat, fIdx) => (
                        <li key={fIdx} className="flex items-center gap-2 text-[11px] text-gray-600 font-medium">
                          <Check className="w-3.5 h-3.5 text-[#00796b] shrink-0" />
                          <span>{feat}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="px-5 sm:px-6 pb-5 pt-3 border-t border-gray-100 flex items-center justify-between bg-gray-50/50">
                  <Link
                    to={`/contact-us/?service=${encodeURIComponent(st.quoteParam)}&message=${encodeURIComponent(`Requesting a quote for ${st.title}.`)}`}
                    className="inline-flex items-center gap-1.5 text-xs font-bold text-[#00796b] hover:text-[#004d40]"
                  >
                    <span>Request Quote</span>
                    <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                  </Link>
                  <Link
                    to="/shutter-types/"
                    className="text-[11px] font-semibold text-gray-600 hover:text-[#00796b] transition-colors"
                  >
                    View Specs &rarr;
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== 7. COMPREHENSIVE SERVICE AREAS (ALL 614 HYDERABAD LOCALITIES) ==================== */}
      <section id="areas" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-8 gap-4">
            <div>
              <div className="text-xs font-bold text-[#00796b] uppercase tracking-wider mb-1">Hyderabad Coverage</div>
              <Link to="/service-areas/">
                <h2 className="text-2xl sm:text-3xl font-extrabold text-[#0f172a] hover:text-[#00796b] transition-colors">
                  We Serve Across All 600+ Hyderabad Localities
                </h2>
              </Link>
              <p className="text-xs sm:text-sm text-gray-500 mt-1 max-w-xl">
                Rapid 60-90 minute emergency technician dispatch available across all zones in Greater Hyderabad, Secunderabad &amp; Cyberabad.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <Link
                to="/service-areas/"
                className="inline-flex items-center justify-center gap-1.5 text-xs font-bold text-[#00796b] hover:text-white border border-[#00796b] hover:bg-[#00796b] px-4 py-2 rounded-lg transition-colors bg-white shadow-2xs shrink-0"
              >
                <span>View All Areas</span>
                <span>&rarr;</span>
              </Link>

              <div className="relative w-full sm:w-64">
                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-3 pointer-events-none" />
                <input
                  type="text"
                  name="area-search"
                  value={areaSearch}
                  onChange={(e) => setAreaSearch(e.target.value)}
                  placeholder="Search any locality..."
                  aria-label="Search any locality"
                  className="w-full pl-9 pr-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:border-[#00796b] bg-gray-50 text-gray-800"
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
            {areas.map((area, idx) => (
              <Link
                key={idx}
                to={area.link}
                className="group rounded-xl overflow-hidden border border-gray-200 bg-white shadow-2xs hover:shadow-md hover:border-[#00796b] transition-all"
              >
                <div className="h-24 w-full overflow-hidden bg-gray-100">
                  <img
                    src={area.image}
                    alt={`Rolling Shutter Service in ${area.name}`}
                    width="200"
                    height="96"
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-2.5 flex items-center justify-between text-xs font-bold text-[#0f172a] group-hover:text-[#00796b]">
                  <span className="truncate">{area.name}</span>
                  <span className="text-[#00796b] group-hover:translate-x-1 transition-transform">&rarr;</span>
                </div>
              </Link>
            ))}
          </div>

          <div className="bg-white border border-gray-200/90 rounded-2xl p-6 sm:p-7 shadow-xs hover:shadow-md transition-shadow">
            {/* Header row with Title and SLAs */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-5 border-b border-gray-100">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#e6faf8] text-[#00796b] flex items-center justify-center shrink-0 mt-0.5">
                  <MapPin className="w-5 h-5" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-extrabold uppercase tracking-wider px-2 py-0.5 rounded bg-emerald-100 text-emerald-800">
                      Rapid Dispatch Fleet
                    </span>
                    <span className="text-xs text-gray-600 font-medium">600+ Hyderabad Localities Covered</span>
                  </div>
                  <h3 className="text-base sm:text-lg font-bold text-[#0f172a] mt-0.5">
                    Select Your Zone or Search Your Specific Neighborhood
                  </h3>
                </div>
              </div>

              {/* SLA Badges */}
              <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-gray-700 bg-gray-50 border border-gray-200/80 px-3 py-1.5 rounded-lg">
                  <Clock className="w-3.5 h-3.5 text-[#00796b]" />
                  <span>60–90 Min SLA</span>
                </span>
                <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-gray-700 bg-gray-50 border border-gray-200/80 px-3 py-1.5 rounded-lg">
                  <ShieldCheck className="w-3.5 h-3.5 text-[#00796b]" />
                  <span>Mobile Service Vans</span>
                </span>
              </div>
            </div>

            {/* Zone Selector Buttons */}
            <div className="flex items-center gap-2 overflow-x-auto py-4 no-scrollbar">
              {['All Key Hubs', 'IT & Cyberabad', 'Secunderabad', 'Central Hyderabad', 'North & Kukatpally', 'East & Industrial'].map((zName) => (
                <button
                  key={zName}
                  type="button"
                  onClick={() => { setSelectedZone(zName); setAreaSearch(''); }}
                  className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all shrink-0 cursor-pointer ${
                    selectedZone === zName && !areaSearch
                      ? 'bg-[#00796b] text-white shadow-xs'
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  }`}
                >
                  {zName}
                </button>
              ))}
            </div>

            {/* Displayed Localities Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2.5 pt-1">
              {displayedLocalities.map((item, idx) => (
                <Link
                  key={idx}
                  to={`/rolling-shutter-repair-in-${item.slug}/`}
                  className="flex items-center justify-between px-3 py-2.5 rounded-xl bg-gray-50/80 hover:bg-[#e6faf8] border border-gray-200/80 hover:border-[#00796b]/50 text-xs font-semibold text-gray-700 hover:text-[#00796b] transition-all group shadow-2xs"
                >
                  <span className="truncate">{item.name}</span>
                  <ArrowRight className="w-3 h-3 text-gray-400 group-hover:text-[#00796b] group-hover:translate-x-0.5 transition-all shrink-0 ml-1" />
                </Link>
              ))}
            </div>

            {/* Bottom Bar: Action buttons and phone */}
            <div className="mt-6 pt-4 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
              <Link
                to="/service-areas/"
                className="inline-flex items-center gap-1.5 font-bold text-[#00796b] hover:text-[#004d40] hover:underline"
              >
                <span>Browse All 600+ Hyderabad Service Areas Directory</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
              <a
                href={BUSINESS_INFO.phoneTel}
                className="inline-flex items-center gap-2 font-bold text-gray-700 hover:text-[#00796b] bg-gray-100 hover:bg-[#e6faf8] px-3.5 py-1.5 rounded-lg transition-colors"
              >
                <Phone className="w-3.5 h-3.5 text-[#00796b]" />
                <span>Emergency Dispatch Helpline: {BUSINESS_INFO.phone}</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== 8. EMERGENCY SERVICE BANNER ==================== */}
      <section className="py-10 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="relative rounded-2xl overflow-hidden bg-gradient-to-r from-[#072438] via-[#09354f] to-[#0c4866] text-white shadow-xl min-h-[300px] flex flex-col justify-between p-6 sm:p-10 md:p-12">
            
            <div className="absolute right-0 top-0 bottom-0 w-full md:w-3/5 pointer-events-none select-none overflow-hidden">
              <img
                src={getAssetUrl('assets/images/shutter/emergency-banner.webp')}
                alt="Emergency rolling shutter repair technician"
                width="800"
                height="300"
                loading="lazy"
                className="w-full h-full object-cover object-center md:object-right opacity-45 md:opacity-75 mix-blend-luminosity"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#072438] via-[#072438]/85 md:via-[#09354f]/60 to-transparent" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#072438]/80 via-transparent to-[#072438]/40" />
            </div>

            <div className="relative z-10 max-w-xl">
              <div className="text-[11px] sm:text-xs font-bold text-[#00bcd4] tracking-[0.2em] uppercase mb-2">
                HOW IT WORKS
              </div>
              <h2 className="text-2xl sm:text-4xl md:text-5xl font-extrabold text-white tracking-tight leading-[1.15] mb-3">
                Need Urgent<br className="hidden sm:inline" /> Shutter Repair?
              </h2>
              <p className="text-xs sm:text-sm text-gray-200/90 leading-relaxed font-normal max-w-lg">
                Our technicians are available for emergency rolling shutter repair across Hyderabad.
              </p>
            </div>

            <div className="relative z-10 mt-8 sm:mt-12 bg-[#051c2c]/85 backdrop-blur-md border border-white/10 rounded-2xl p-4 sm:p-5 flex flex-col lg:flex-row items-center justify-between gap-6 shadow-2xl">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 sm:gap-6 w-full lg:w-auto flex-1">
                <div className="flex items-center gap-3.5 sm:border-r sm:border-white/15 sm:pr-4">
                  <div className="w-10 h-10 rounded-full border border-[#00bcd4]/50 bg-[#00bcd4]/10 text-[#00bcd4] flex items-center justify-center shrink-0">
                    <Clock className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="text-xs sm:text-sm font-bold text-white leading-tight">Quick Response</div>
                    <div className="text-[11px] text-gray-300 mt-0.5">We reach you fast</div>
                  </div>
                </div>

                <div className="flex items-center gap-3.5 sm:border-r sm:border-white/15 sm:pr-4">
                  <div className="w-10 h-10 rounded-full border border-[#00bcd4]/50 bg-[#00bcd4]/10 text-[#00bcd4] flex items-center justify-center shrink-0">
                    <MapPin className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="text-xs sm:text-sm font-bold text-white leading-tight">Available Across</div>
                    <div className="text-[11px] text-gray-300 mt-0.5">All Hyderabad Areas</div>
                  </div>
                </div>

                <div className="flex items-center gap-3.5">
                  <div className="w-10 h-10 rounded-full border border-[#00bcd4]/50 bg-[#00bcd4]/10 text-[#00bcd4] flex items-center justify-center shrink-0">
                    <Users className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="text-xs sm:text-sm font-bold text-white leading-tight">Experienced Team</div>
                    <div className="text-[11px] text-gray-300 mt-0.5">Trained professionals</div>
                  </div>
                </div>
              </div>

              <div className="w-full sm:w-auto shrink-0 flex justify-end">
                <a
                  href={BUSINESS_INFO.phoneTel}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-[#00796b] hover:bg-[#004d40] text-white px-6 py-3.5 rounded-xl font-bold text-xs sm:text-sm shadow-lg shadow-[#00796b]/20 hover:shadow-[#00796b]/30 transition-all duration-200 active:scale-95 shrink-0"
                >
                  <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
                    <Phone className="w-4 h-4 fill-white text-white" />
                  </div>
                  <div className="text-left">
                    <div className="text-[10px] uppercase font-bold text-white/90 leading-tight">Call Now</div>
                    <div className="text-xs sm:text-sm font-extrabold tracking-wide">{BUSINESS_INFO.phone}</div>
                  </div>
                </a>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ==================== 9. RECENT PROJECTS ==================== */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-10 gap-4">
            <div>
              <div className="text-xs font-bold text-[#00796b] uppercase tracking-wider mb-1">Our Recent Works</div>
              <Link to="/our-works/">
                <h2 className="text-2xl sm:text-3xl font-extrabold text-[#0f172a] hover:text-[#00796b] transition-colors">Latest Projects</h2>
              </Link>
            </div>
            <Link
              to="/our-works/"
              className="inline-flex items-center gap-1.5 text-xs font-bold text-[#00796b] hover:text-[#004d40] border border-[#00796b] px-4 py-2 rounded-md transition-colors bg-white shadow-2xs self-start sm:self-auto"
            >
              <span>View All Works</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {projects.map((proj, idx) => (
              <Link to={proj.link} key={idx} className="group block rounded-xl border border-gray-200 overflow-hidden bg-white shadow-2xs hover:shadow-md hover:border-[#00796b] transition-all">
                <div className="h-44 w-full overflow-hidden bg-gray-100">
                  <img
                    src={proj.image}
                    alt={proj.title}
                    width="240"
                    height="176"
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-3.5">
                  <h3 className="text-xs font-bold text-[#0f172a] mb-1">{proj.title}</h3>
                  <div className="flex items-center gap-1 text-[11px] text-gray-500">
                    <MapPin className="w-3 h-3 text-[#00796b]" />
                    <span>{proj.loc}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== 10. TESTIMONIALS ==================== */}
      <section className="py-16 bg-[#f7f9fc]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-8 gap-4">
            <div>
              <div className="text-xs font-bold text-[#00796b] uppercase tracking-wider mb-1">Customer Reviews</div>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-[#0f172a]">What Our Hyderabad Clients Say</h2>
              <p className="text-xs text-gray-500 mt-1">Verified reviews from shop owners, factory managers, and corporate complexes across Hyderabad.</p>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-500 font-medium hidden sm:inline">
                Page {activeTestimonialIdx + 1} of {totalTestimonialPages} ({testimonialsData.length} Reviews)
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setActiveTestimonialIdx((prev) => (prev === 0 ? totalTestimonialPages - 1 : prev - 1))}
                  className="w-9 h-9 rounded-xl border border-gray-200 bg-white flex items-center justify-center text-gray-700 hover:text-[#00796b] hover:border-[#00796b] shadow-2xs transition-colors cursor-pointer"
                  aria-label="Previous testimonials"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTestimonialIdx((prev) => (prev === totalTestimonialPages - 1 ? 0 : prev + 1))}
                  className="w-9 h-9 rounded-xl border border-gray-200 bg-white flex items-center justify-center text-gray-700 hover:text-[#00796b] hover:border-[#00796b] shadow-2xs transition-colors cursor-pointer"
                  aria-label="Next testimonials"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Testimonial Cards Grid (3 at a time) */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {currentTestimonials.map((t) => (
              <div
                key={t.id}
                className="bg-white p-6 rounded-2xl border border-gray-200/90 shadow-2xs hover:shadow-md hover:border-[#00796b]/40 transition-all duration-300 flex flex-col justify-between"
              >
                <div>
                  {/* Top Meta: Locality + Verified Badge */}
                  <div className="flex items-center justify-between gap-2 mb-4">
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-[#00796b]/10 text-[#00796b] text-[10px] font-bold">
                      <CheckCircle2 className="w-3 h-3" />
                      <span>Verified Booking</span>
                    </span>
                    <span className="text-[11px] text-gray-500 font-medium">{t.date}</span>
                  </div>

                  {/* Service Performed Tag */}
                  <div className="mb-3">
                    <span className="text-[11px] font-semibold text-gray-700 bg-gray-100 px-2 py-0.5 rounded-md inline-block">
                      {t.service}
                    </span>
                  </div>

                  {/* 5 Stars */}
                  <div className="flex items-center gap-0.5 text-amber-400 mb-3">
                    {[...Array(t.rating || 5)].map((_, i) => (
                      <Star key={i} className="w-3.5 h-3.5 fill-current" />
                    ))}
                  </div>

                  {/* Review Text */}
                  <p className="text-xs text-gray-600 leading-relaxed italic line-clamp-4">
                    &ldquo;{t.text}&rdquo;
                  </p>
                </div>

                {/* Author Info */}
                <div className="flex items-center gap-3 pt-5 mt-5 border-t border-gray-100">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#00796b] to-[#00bcd4] text-white font-extrabold text-sm flex items-center justify-center shrink-0 shadow-xs">
                    {t.initial}
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-xs font-bold text-[#0f172a] truncate">{t.name}</h3>
                    <p className="text-[11px] text-gray-500 truncate">{t.role}</p>
                    <p className="text-[10px] text-gray-500 truncate">{t.locality}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination Indicator Dots */}
          <div className="flex items-center justify-center gap-1 mt-8">
            {[...Array(totalTestimonialPages)].map((_, pageIdx) => (
              <button
                key={pageIdx}
                type="button"
                onClick={() => setActiveTestimonialIdx(pageIdx)}
                className="p-2 flex items-center justify-center cursor-pointer"
                aria-label={`Go to testimonial page ${pageIdx + 1}`}
              >
                <span
                  className={`block transition-all duration-300 rounded-full ${
                    pageIdx === activeTestimonialIdx
                      ? 'w-8 h-2 bg-[#00796b]'
                      : 'w-2 h-2 bg-gray-300 hover:bg-gray-400'
                  }`}
                />
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== 11. BLOG / GUIDES & UPDATES ==================== */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-10 gap-4">
            <div>
              <div className="text-xs font-bold text-[#00796b] uppercase tracking-wider mb-1">Latest From Our Blog</div>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-[#0f172a]">Tips, Guides &amp; Updates</h2>
              <p className="text-xs sm:text-sm text-gray-500 mt-1">
                Practical shutter maintenance tips, troubleshooting diagnostics, and commercial security insights from certified technicians.
              </p>
            </div>
            <Link
              to="/blog/"
              className="inline-flex items-center gap-1.5 text-xs font-bold text-[#00796b] hover:text-[#00796b] border border-[#00796b] px-4 py-2 rounded-md transition-colors bg-white shadow-2xs self-start sm:self-auto shrink-0"
            >
              <span>View All Blog Posts</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            {featuredBlogs.map((b, idx) => (
              <Link
                key={idx}
                to={b.link}
                className="group rounded-2xl border border-gray-200 overflow-hidden bg-white shadow-xs hover:shadow-xl hover:border-[#00796b]/50 transition-all duration-300 flex flex-col justify-between"
              >
                <div>
                  <div className="relative h-52 sm:h-56 w-full overflow-hidden bg-gray-100">
                    <img
                      src={b.image}
                      alt={b.title}
                      width="380"
                      height="224"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                      onError={(e) => handleImageError(e, 'assets/images/shutter/blog-1.jpg')}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-60 group-hover:opacity-40 transition-opacity" />
                    <div className="absolute top-3 left-3">
                      <span className="text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full shadow-xs backdrop-blur-md bg-white/95 text-[#00796b] border border-white/60">
                        {b.category}
                      </span>
                    </div>
                    <div className="absolute bottom-3 right-3">
                      <span className="text-[10px] font-semibold text-white/95 bg-black/60 backdrop-blur-xs px-2.5 py-1 rounded-md">
                        {b.readTime}
                      </span>
                    </div>
                  </div>

                  <div className="p-5 sm:p-6">
                    <div className="flex items-center gap-2 text-[11px] text-gray-500 font-semibold mb-2">
                      <span>{b.date}</span>
                      <span>•</span>
                      <span>By {b.author}</span>
                    </div>
                    <h3 className="text-base font-bold text-[#0f172a] group-hover:text-[#00796b] transition-colors leading-snug mb-2 line-clamp-2">
                      {b.title}
                    </h3>
                    <p className="text-xs text-gray-500 leading-relaxed line-clamp-2">
                      {b.excerpt}
                    </p>
                  </div>
                </div>

                <div className="px-5 sm:px-6 pb-5 pt-3 border-t border-gray-100 flex items-center justify-between text-xs font-bold text-[#00796b] bg-gray-50/40">
                  <span>Read Full Guide</span>
                  <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1.5 transition-transform" />
                </div>
              </Link>
            ))}
          </div>

          {/* Knowledge Base CTA Strip */}
          <div className="mt-10 bg-gradient-to-r from-gray-50 via-white to-gray-50 border border-gray-200/90 rounded-2xl p-5 sm:p-6 flex flex-col sm:flex-row items-center justify-between gap-4 shadow-2xs">
            <div className="flex items-center gap-3.5">
              <div className="w-10 h-10 rounded-xl bg-[#00796b]/10 text-[#00796b] flex items-center justify-center shrink-0">
                <BookOpen className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs sm:text-sm font-bold text-[#0f172a]">
                  Looking for more shutter troubleshooting, motor wiring diagrams or maintenance schedules?
                </p>
                <p className="text-[11px] text-gray-500 mt-0.5">
                  Browse our complete knowledge base of commercial, industrial, and retail shutter resources.
                </p>
              </div>
            </div>
            <Link
              to="/blog/"
              className="inline-flex items-center gap-1.5 text-xs font-bold text-white bg-[#00796b] hover:bg-[#004d40] px-4 py-2.5 rounded-lg transition-colors shrink-0 shadow-2xs"
            >
              <span>Explore All Guides</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* ==================== 12. FREQUENTLY ASKED QUESTIONS (2-COLUMNS ACCORDION) ==================== */}
      <section className="py-16 bg-[#f8fafc] border-t border-gray-200/80">
        <div className="max-w-7xl mx-auto px-4">
          <SectionHeader
            tag="Got Questions? We Have Answers"
            title="Frequently Asked Questions About Shutter Repair"
            subtitle="Everything you need to know about pricing, emergency response, genuine motor spares, and warranty coverage in Hyderabad."
          />

          {/* 2-Columns Responsive Accordion Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 items-start mt-8">
            <Accordion items={faqCol1} />
            <Accordion items={faqCol2} />
          </div>

          <div className="mt-10 text-center">
            <span className="text-xs text-gray-500">Still have a question? </span>
            <Link to="/contact-us/" className="text-xs font-bold text-[#00796b] hover:text-[#004d40] hover:underline">
              Contact our 24/7 Hyderabad helpline &rarr;
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
